/** @jsxImportSource @emotion/react */
import { Header } from '@quickbit/backoffice-core-frontend';
import {
  Table,
  Typography,
  useTheme,
  useForm,
  Pagination,
  SearchInput,
  Icon,
} from '@quickbit/qb-design-system';
import { every as _every, mapValues as _mapValues } from 'lodash';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAsyncFn, useDebounce, useSessionStorage } from 'react-use';
import {
  ProcessingUserFilters,
  SearchProcessingUsersResponse,
  searchProcessingUsers,
} from '../api';
import { useSetSelected } from '../context';
import { getLabel } from '../types';

const PAGE_SIZE = 16;

const onlyEmailAndUserIdHaveValue = (
  filters: ProcessingUserFilters
): boolean => {
  return (
    Boolean(filters.userId) ||
    Boolean(filters.email) ||
    _every(filters, (value, key) =>
      key === 'userId' || key === 'email' ? Boolean(value) : !value
    )
  );
};

export const ProcessingUserList = () => {
  const { spacing, palette } = useTheme();
  const setSelected = useSetSelected();
  const navigate = useNavigate();

  const [filterStore, setFilterStore] = useSessionStorage<{
    filters: ProcessingUserFilters;
    pagination: {
      page: number;
      size: number;
    };
  } | null>('qbProcessingUserFilterStore');

  const [page, setPage] = useState(filterStore?.pagination.page || 1);

  const { fields, setters } = useForm<ProcessingUserFilters>(
    {
      userId: filterStore?.filters.userId || null,
      email: filterStore?.filters.email || null,
      oldUserId: filterStore?.filters.oldUserId || null,
      externalReference: filterStore?.filters.externalReference || null,
    },
    {}
  );

  const headers = [
    { label: getLabel('uuid'), cellKey: 'uuid', sort: false },
    { label: getLabel('email'), cellKey: 'email', sort: false },
    { label: getLabel('oldUuid'), cellKey: 'oldUuid', sort: false },
    {
      label: getLabel('externalReference'),
      cellKey: 'externalReference',
      sort: false,
    },
  ];

  const [value, setSearchStore] =
    useSessionStorage<SearchProcessingUsersResponse | null>(
      'qbProcessingUserSearchResult'
    );

  const [{ loading }, search] = useAsyncFn(async () => {
    const filters: ProcessingUserFilters = _mapValues(fields, (v) => v?.trim());
    const appliedFilters = {
      filters,
      pagination: {
        page: onlyEmailAndUserIdHaveValue(filters) ? 1 : page,
        size: PAGE_SIZE,
      },
    };

    const data = await searchProcessingUsers(appliedFilters);
    setFilterStore(appliedFilters);
    setSearchStore(data);
    return data;
  }, [page, fields]);

  useDebounce(
    () => {
      void search();
    },
    500,
    [search]
  );

  const rows = useMemo(() => {
    if (!value) return [];

    // A workaround to reset pagination if the data rows are less than the page size
    setPage((state) => (value.aggregatedData.count <= PAGE_SIZE ? 1 : state));

    return Object.values(value.processingUsers).map((user) => ({
      value: {
        uuid: user.userId,
        email: user.email,
        oldUuid: user.oldUserId,
        externalReference: user.profile?.externalReference,
      },
    }));
  }, [value]);

  const handleRowClicked = (row: any) => {
    if (!value) return;
    const selectedUser = value.processingUsers.filter(
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      (p) => p.userId === row.uuid
    )[0];
    setSelected(selectedUser);

    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
    void navigate(row.uuid);
  };

  return (
    <>
      <Header>
        <div
          css={{
            display: 'grid',
            gridTemplateColumns: '1fr repeat(4, auto)',
            alignItems: 'center',
            gap: spacing.xxs,
          }}
        >
          <Typography variant="subtitle">Processing</Typography>
        </div>
      </Header>
      <div
        css={{
          marginTop: spacing.xxs,
          display: 'grid',
          gridTemplateColumns: 'auto auto auto auto auto',
          gap: spacing.xxxs,
          alignItems: 'center',
          zIndex: 1,
        }}
      >
        <SearchInput
          placeholder="User ID"
          value={fields.userId ?? ''}
          onChange={(e) => setters.userId?.set(e.target.value)}
        />
        <SearchInput
          placeholder="Email"
          value={fields.email ?? ''}
          onChange={(e) => setters.email?.set(e.target.value)}
        />
        <SearchInput
          placeholder="Old User Id"
          value={fields.oldUserId ?? ''}
          onChange={(e) => setters.oldUserId?.set(e.target.value)}
        />
        <SearchInput
          placeholder="External Reference"
          value={fields.externalReference ?? ''}
          onChange={(e) => setters.externalReference?.set(e.target.value)}
        />
        {loading && (
          <Icon
            name="spinner"
            className={{ svg: { color: palette.grey.default } }}
          />
        )}
      </div>
      {value && (
        <>
          <div css={{ marginTop: spacing.xxs }}>
            <Table
              headers={headers}
              rows={rows}
              onRowClick={handleRowClicked}
            />
          </div>
          <div css={{ marginTop: spacing.xxs }}>
            <Pagination
              currentPage={page}
              onChange={setPage}
              numberOfElements={value.aggregatedData.count}
              pageSize={PAGE_SIZE}
            />
          </div>
        </>
      )}
    </>
  );
};
